import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    OnInit,
    output,
    signal,
    ViewEncapsulation,
    WritableSignal,
    ElementRef,
} from '@angular/core';
import { IInsertMediaResult, IInsertMediaUploadItem } from '@fi-shared/fi-insert-media/fi-insert-media';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { FiFileUploadState } from '@fi-shared/fi-insert-media/fi-file-upload-enums';
import {
    IFileUploadData,
    IFileUploadFailedData,
    TProcessMediaBankFileFn,
} from '@fi-shared/fi-insert-media/fi-file-upload-d';
import { FiFileUpload2Service } from '@fi-shared/fi-insert-media/fi-file-upload2.service';
import { FileType } from '@fi-shared/types';
import { isDefined } from '@fi-shared/utils';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';

@Component({
    selector: 'fi-file-upload',
    templateUrl: './fi-file-upload.component.html',
    styleUrl: './fi-file-upload.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-file-upload' },
    standalone: false,
})
export class FiFileUploadComponent implements OnInit {
    @Input() allowedFileTypes: FileType[];
    @Input() processMediaBankFileFn: TProcessMediaBankFileFn = (mbRef) => Promise.resolve();
    @Input() multiple: boolean = false;
    @Input() partOfUploadProcess = 0.8;
    @Input() iconsSize: number = 65;
    @Input() wrapperElementRef: ElementRef;

    clearUploadData = output<void>();

    protected filesUploadData: WritableSignal<IFileUploadData>[] = [];

    private fiFileUpload2Service = inject(FiFileUpload2Service);
    private fiDialogsService = inject(FiDialogsService);
    private appTextService = inject(AppTextService);

    ngOnInit(): void {
        // for tests
        //this.multiple = true;
        /*const data1 = signal<IFileUploadData>({
            fileName: 'test_1.xlsx',
            state: FiFileUploadState.Completed,
            size: 25000,
            progress: 80,
            abortUploadFn: () => {},
        });
        const data2 = signal<IFileUploadData>({
            fileName: 'test_1.xlsx',
            state: FiFileUploadState.InProgress,
            size: 40000,
            progress: 40,
            abortUploadFn: () => {},
        });
        const data3 = signal<IFileUploadData>({
            fileName: 'test_1.xlsx',
            state: FiFileUploadState.Failed,
            size: 60000,
            progress: 50,
            abortUploadFn: () => {},
        });
        this.filesUploadData = [data1, data2, data3];*/
    }

    protected showUploadPlaceholder() {
        return this.filesUploadData.length === 0 || this.multiple;
    }

    protected onFilesInsertedFromPopup(
        result: IInsertMediaResult,
        existingFileUploadData?: WritableSignal<IFileUploadData>,
    ) {
        const mbRefs: IMediaBankFileReference[] = this.multiple
            ? result.mediaBankFileReferences
            : [result.mediaBankFileReferences[0]];
        mbRefs.forEach((mbRef) => {
            const fileUploadData = this.checkInitFileUploadData(existingFileUploadData);
            this.initFileDataFromMediaLibrary(mbRef, fileUploadData);
            this.processMediaBankFile(mbRef, fileUploadData);
        });
    }

    protected onFilesInsertedViaDnd(files: File[], existingFileUploadData?: WritableSignal<IFileUploadData>): void {
        // upload via dnd
        const dndFiles: File[] = this.multiple ? files : [files[0]];
        dndFiles.forEach((file) => {
            const fileUploadData = this.checkInitFileUploadData(existingFileUploadData);
            this.initFileDataFromBlob(file, fileUploadData);
            this.fiFileUpload2Service
                .silentUploadFiles([file], {
                    onUploadProgress: (mf, progress) => this.onImportProgress(progress, fileUploadData),
                    onUploadStarted: (uploadItem: IInsertMediaUploadItem) => {
                        this.updateCancelUploadFn(uploadItem, fileUploadData);
                    },
                    skipLoadingAnimation: true,
                })
                .then(
                    (mediaBankReferences: IMediaBankFileReference[]) => {
                        this.processMediaBankFile(mediaBankReferences[0], fileUploadData);
                    },
                    () =>
                        this.onFailedUpload(
                            {
                                file: file,
                            },
                            fileUploadData,
                        ),
                );
        });
    }

    private checkInitFileUploadData(fileUploadData?: WritableSignal<IFileUploadData>): WritableSignal<IFileUploadData> {
        if (isDefined(fileUploadData)) {
            // retry mode, just return current data
            return fileUploadData;
        }
        const newFileUploadData = signal<IFileUploadData>(undefined);
        if (this.multiple || !this.filesUploadData.length) {
            this.filesUploadData.push(newFileUploadData);
        } else {
            this.filesUploadData[0] = newFileUploadData;
        }
        return newFileUploadData;
    }

    private processMediaBankFile(
        mediaBankFileReference: IMediaBankFileReference,
        fileUploadData: WritableSignal<IFileUploadData>,
    ) {
        this.processMediaBankFileFn(mediaBankFileReference).then(
            () => {
                fileUploadData.update((current) => {
                    return {
                        ...current,
                        state: FiFileUploadState.Completed,
                        progress: 100,
                    };
                });
            },
            () => {
                this.onFailedUpload(
                    {
                        mediaBankFileReference: mediaBankFileReference,
                    },
                    fileUploadData,
                );
            },
        );
    }

    //#region file data update
    private initFileDataFromMediaLibrary(
        mediaBankFileReference: IMediaBankFileReference,
        fileUploadData: WritableSignal<IFileUploadData>,
    ) {
        fileUploadData.update(() => {
            return {
                state: FiFileUploadState.InProgress,
                fileName: mediaBankFileReference.Name + mediaBankFileReference.Extension,
                size: mediaBankFileReference.Size,
                progress: this.partOfUploadProcess * 100,
                abortUploadFn: undefined,
                mediaBankFileReference: mediaBankFileReference,
            };
        });
    }

    private initFileDataFromBlob(file: File, fileUploadData: WritableSignal<IFileUploadData>) {
        fileUploadData.update(() => {
            return {
                state: FiFileUploadState.InProgress,
                fileName: file.name,
                size: file.size,
                progress: 0,
                abortUploadFn: undefined,
                file: file,
            };
        });
    }

    private onImportProgress(progress: number, fileUploadData: WritableSignal<IFileUploadData>) {
        fileUploadData.update((fileImportData) => {
            return {
                ...fileImportData,
                progress: progress * this.partOfUploadProcess,
            };
        });
    }

    private onFailedUpload(failedData: IFileUploadFailedData, fileUploadData: WritableSignal<IFileUploadData>) {
        fileUploadData.update((current) => {
            return {
                ...current,
                state: FiFileUploadState.Failed,
                lastFailedData: failedData,
            };
        });
    }

    protected onRetryUpload(fileUploadData: WritableSignal<IFileUploadData>) {
        fileUploadData.update((current) => {
            return {
                ...current,
                state: FiFileUploadState.InProgress,
                progress: 0,
            };
        });
        const uploadData = fileUploadData();
        if (uploadData.file) {
            // retrying dnd
            this.onFilesInsertedViaDnd([uploadData.file], fileUploadData);
        } else if (uploadData.mediaBankFileReference) {
            // retrying post process media file (import checklist from excel etc)
            this.processMediaBankFile(uploadData.mediaBankFileReference, fileUploadData);
        }
    }

    private updateCancelUploadFn(uploadItem: IInsertMediaUploadItem, fileUploadData: WritableSignal<IFileUploadData>) {
        fileUploadData.update((current) => {
            return {
                ...current,
                abortUploadFn: () => {
                    // now we have uploadItem and can cancel upload on button click etc
                    uploadItem.abortUpload();
                },
            };
        });

        // cancel upload after 200ms, for tests
        /*setTimeout(() => {
            this.fileUploadData().abortUploadFn();
        }, 200);*/
    }

    protected onClearUploadData(fileUploadData: WritableSignal<IFileUploadData>) {
        /*this.fiDialogsService.custom({
            headerText: this.appTextService.get('mediabank.delete_file'),
            messageText: this.appTextService.get('media.delete_file_confirm_text'),
            yesButtonText: this.appTextService.get('common.delete'),
            noButtonText: this.appTextService.get('common.cancel'),
            config: {
                position: {
                    left: '10%',
                },
            },
        });*/

        this.fiDialogsService
            .error({
                headerText: this.appTextService.get('mediabank.delete_file'),
                messageText: this.appTextService.get('media.delete_file_confirm_text'),
                yesButtonText: this.appTextService.get('common.delete'),
                noButtonText: this.appTextService.get('common.cancel'),
                isNoButtonPrimaryColor: true,
                preventUppercaseButtonLabel: true,
                //isTopCloseButtonShown: false,
                config: {
                    panelClass: ['fi-rounded-dialog-panel', 'fi-dialog-no-button-primary-red'],
                    autoFocus: false,
                },
            })
            .onYesButtonClick(() => {
                const idx = this.filesUploadData.indexOf(fileUploadData);
                this.filesUploadData.splice(idx, 1);
                //fileUploadData.update(() => undefined);
                this.clearUploadData.emit();
            });
    }

    protected onCancelUpload(fileUploadData: WritableSignal<IFileUploadData>) {
        fileUploadData().abortUploadFn();
        this.clearUploadData.emit();
    }

    //#endregion file data update
}
